import ConfirmDeleteModalUI from '@/components/common/ConfirmDeleteModalUI/ConfirmDeleteModalUI'
import { debounce } from 'lodash'
import { useConfigUI } from '../../context'

import useDeleteFlowSensor from '../../hooks/hydraulic/flowSensor/useDeleteFlowSensor'
import useWaterSourceUsage from '../../hooks/watersource/useWaterSourceUsage'
import toast from 'react-hot-toast'
import useTranslation from '@/hooks/useTranslation'
import { getApiErrorMessage } from '@/helpers/notify'

interface DeleteModalProps {
  id?: string
  interfaceId?: string
}

const DeleteFlowSensorView = ({ id, interfaceId }: DeleteModalProps) => {
  const { t } = useTranslation()
  const { closeModal } = useConfigUI()
  const {
    sensorUsedBy,
    ready: usageReady,
    error: usageError,
  } = useWaterSourceUsage(Number(interfaceId))
  // Until the water sources are read the map is empty, which would let a
  // used flow sensor through: Confirm waits, and a failed read refuses
  // (SCRUM-1032 review).
  const usageLoading = !usageReady && !usageError

  const { mutate: deleteModal, isLoading: loadingDeleteFlowSensor } =
    useDeleteFlowSensor()

  const handleDeleteFlowSensor = debounce(async () => {
    if (!id) return
    if (!interfaceId) return
    if (!usageReady) {
      toast.error(getApiErrorMessage(usageError, t('somethingWentWrong')))
      closeModal()
      return
    }
    // A water source points at the flow sensor it meters through, so one
    // it uses is refused here rather than at the delete endpoint.
    const usedBy = sensorUsedBy.get(Number(id))
    if (usedBy?.length) {
      toast.error(t('flowSensorUsedByWaterSource', { names: usedBy.join(', ') }))
      closeModal()
      return
    }
    deleteModal({ interfaceId, id })
  }, 300)

  return (
    <ConfirmDeleteModalUI
      title={t('DeleteFlowSensor')}
      text={t('flowSensorDeleteConfirmation')}
      onClose={closeModal}
      onConfirm={handleDeleteFlowSensor}
      isLoading={loadingDeleteFlowSensor || usageLoading}
    />
  )
}

export default DeleteFlowSensorView
